import _ from 'underscore';

import { mapFbHours } from './business-hours-mapping';
import { generateSectionWithComponent, generateId } from './generate-section-with-component';
import {
  generateNewCmpsFromExistingData,
  getEnabledComponentsConfig,
  getOverrideCompConfigMapping,
  removeDuplicatedComponents,
} from './component-utils';
import { createWebsiteSourceData } from './website-source-data-util';
import { getTimeZoneId } from './site-config-reader';
import { WEBSITE_SOURCE_DATA_KEY_MAPPING } from './site-config-structure';

const fbRelatedComponents = [
  'review',
  'update',
];

/**
 * Generate attributes from websiteSourceData
 * @param {Object} websiteSourceData website related data
 * @param {Object} serverContext server context
 * @returns {Array} A generated attributes
 */
const websiteSourceDataToAttributes = (websiteSourceData = {}, serverContext) => {
  const overridedCompConfigMapping = getOverrideCompConfigMapping(websiteSourceData);
  // Map website source data to pages
  const pages = [{
    id: generateId('pge'),
    components: [],
  }];

  const enabledComponentsConfig = getEnabledComponentsConfig(serverContext);

  _.each(enabledComponentsConfig, (comp) => {
    const { type, order } = comp;

    if (!fbRelatedComponents.includes(type)) {
      const sectionConfig = generateSectionWithComponent({
        componentType: type,
        sectionIndex: order,
      });

      // Will always has return
      const config = _.find(sectionConfig, item => item.type === type);

      // We take what are provided and what are required.
      if (overridedCompConfigMapping[type]) {
        _.extend(config, overridedCompConfigMapping[type]);
        pages[0].components.push(...sectionConfig);
      } else if (comp.required) {
        pages[0].components.push(...sectionConfig);
      }
    }
  });

  // Map website source data to dataSource
  const dataSource = {
    businessCategories: websiteSourceData.Categories,
    businessDescription: websiteSourceData.Description,
    businessName: websiteSourceData.BusinessName,
    emails: [websiteSourceData.Email],
    locationInfo: websiteSourceData.SingleLineAddress,
    logoImageId: websiteSourceData.LogoImageId,
    phoneNumber: websiteSourceData.PhoneNumber,
  };

  // BP user navigate to main page directly, so entry wizard will not be shown again
  const siteContextInfo = {
    hasEntryWizardShown: true,
    hasQualtricsPopupShown: false,
  };

  return [
    {
      Key: 'dataSource',
      Value: JSON.stringify(dataSource),
    },
    {
      Key: 'pages',
      Value: JSON.stringify(pages),
    },
    {
      Key: 'theme',
      Value: JSON.stringify({ themeId: 'theme_gray' }),
    },
    {
      Key: 'siteContextInfo',
      Value: JSON.stringify(siteContextInfo),
    },
  ];
};

/**
 * Update config based on existing config and serverContext when pilot is turn ON and OFF
 * @param {Object} siteConfig The complete data for a smart page
 * @param {Object} serverContext Server Context
 * @returns {Array} The updated components.
 */
const updateComponents = (siteConfig, serverContext) => {
  let allComponents = (siteConfig.pages && siteConfig.pages[0]
    && siteConfig.pages[0].components) || [];

  if (_.isEmpty(allComponents)) {
    return siteConfig;
  }

  const newSiteConfig = { ...siteConfig };
  const enabledComponentsConfig = getEnabledComponentsConfig(serverContext);

  allComponents = removeDuplicatedComponents(allComponents);
  newSiteConfig.pages[0].components = generateNewCmpsFromExistingData(
    allComponents,
    enabledComponentsConfig
  );

  return newSiteConfig;
};

/**
 * Create new websiteSourceData if it doesn't exist, then update it with serverContext
 * @param {Object} siteConfig Complete data for Smart Page
 * @param {Object} serverContext ServerContext
 * @returns {Object} newWebsiteSourceData
 */
const updateWebsiteSourceData = (siteConfig, serverContext) => {
  const {
    websiteSourceData,
    dataSource,
    pages,
  } = siteConfig;
  let newWebsiteSourceData = websiteSourceData || {};

  if (_.isEmpty(websiteSourceData) && !_.isEmpty(dataSource) && !_.isEmpty(pages)) {
    newWebsiteSourceData = createWebsiteSourceData(siteConfig, serverContext);
  }

  newWebsiteSourceData[WEBSITE_SOURCE_DATA_KEY_MAPPING.TimeZoneId] = getTimeZoneId(
    serverContext,
    siteConfig
  );

  return newWebsiteSourceData;
};

/**
 * Convert model used in MT to Siteconfig in UI side.
 * @param {Object} model A complete data get from MT call
 * @param {Object} serverContext Server context
 * @returns {Object} Site config used in UI side
 */
export const modelToSiteConfig = (model, serverContext = {}) => {
  const siteConfig = {
    id: model.Id,
    name: model.Name,
    status: model.Status,
    subDomain: model.SubDomain,
    siteSuffix: model.SiteSuffix,
    uetTagId: model.UetTagId,
    isMTagMigrated: model.IsMTagMigrated,
    associations: model.Associations,
    isEditorialRejected: model.IsEditorialRejected,
    smartPageDataContext: model.SmartPageDataContext,
    domainDetail: model.DomainDetail,
    websiteSourceData: model.WebsiteSourceData,
    migrationContext: model.WSDMigrationContext,
    previewId: model.PreviewId,
    images: model.Images,
    localImageUrl: '',
    snapshots: {},
  };

  // Bing Places created website may not have Attributes property,
  // creating the Attributes with WebsiteSourceData will be needed.
  const attrs = !_.isEmpty(model.Attributes)
    ? model.Attributes
    : websiteSourceDataToAttributes(model.WebsiteSourceData);
  _.each(attrs, (attr) => {
    siteConfig[attr.Key] = JSON.parse(attr.Value);
  });

  const snapshotsAttrs = model.Snapshots;
  _.each(snapshotsAttrs, (snapshotAttr) => {
    siteConfig.snapshots[snapshotAttr.Key] = JSON.parse(snapshotAttr.Value);
  });

  // Existing smart pages won't have migrationContext
  // Setting a default value allows to revert the migration status
  // of a component successfully.
  siteConfig.snapshots.migrationContext = siteConfig.snapshots.migrationContext || {};

  const newSiteConfig = updateComponents(siteConfig, serverContext);
  newSiteConfig.websiteSourceData = updateWebsiteSourceData(newSiteConfig, serverContext);

  return newSiteConfig;
};

/**
 * Convert Facebook page data to Siteconfig.
 * @param {*} fbPage Facebook page related data
 * @returns {Object} A new site config used in UI side.
 */
export const facebookPageToSiteConfig = (fbPage) => {
  const siteInfoData = {
    facebookId: fbPage.Id,
    facebookUserId: fbPage.FbUserId,
    businessName: fbPage.Name || '',
    businessCategories: _.pluck(fbPage.CategoryList, 'Name') || [],
    businessDescription: fbPage.About || '',
    businessHours: mapFbHours(fbPage.Hours),
    phoneNumber: fbPage.Phone,
    locationInfo: fbPage.SingleLineAddress,
    callToActions: fbPage.CallToActions,
    profilePicture: fbPage.Picture && fbPage.Picture.Url,
    posts: _.isEmpty(fbPage.Posts) ? []
      : fbPage.Posts.filter(post => post.FullPicture || post.Message),
    reviews: fbPage.Ratings,
    coverImage: fbPage.Cover && fbPage.Cover.Source,
    emails: fbPage.Emails,
    photos: fbPage.Photos,
    fbErrors: fbPage.FbErrors,
  };

  return siteInfoData;
};
